import { useEffect } from 'react';
import { SUPPORTED_UI_LOCALES } from '../locale';

/**
 * Emit `<link rel="canonical">` + `<link rel="alternate" hreflang="...">`
 * for the current page. Pass a locale-agnostic path like `/support` or
 * `/story/123/completed` — the hook expands it to every supported UI
 * locale and points canonical to the current locale's variant.
 *
 * x-default alternate points to the unprefixed URL, which is handled by
 * LegacyLocaleRedirect (sending the user to their preferred locale).
 *
 * Explore has its own, richer head manager because it has two axes
 * (uiLocale × contentLang); everything else uses this hook.
 */
export function useHreflangHead(currentLocale: string, localelessPath: string): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const origin = window.location.origin;
    const canonicalHref = `${origin}/${currentLocale}${localelessPath === '/' ? '' : localelessPath}`;

    const setLink = (rel: string, hreflang: string | null, href: string) => {
      const sel = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector<HTMLLinkElement>(sel);
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.href = href;
    };
    const removeAll = (sel: string) => {
      document.head.querySelectorAll(sel).forEach((el) => el.remove());
    };

    setLink('canonical', null, canonicalHref);
    removeAll('link[rel="alternate"][hreflang]');
    for (const ui of SUPPORTED_UI_LOCALES) {
      setLink('alternate', ui, `${origin}/${ui}${localelessPath === '/' ? '' : localelessPath}`);
    }
    setLink(
      'alternate',
      'x-default',
      `${origin}${localelessPath === '/' ? '/' : localelessPath}`,
    );

    return () => {
      removeAll('link[rel="alternate"][hreflang]');
      removeAll('link[rel="canonical"]');
    };
  }, [currentLocale, localelessPath]);
}
